import React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { formatTimestamp } from '../helpers';

/**
 * Renders the participants table inside the meeting-details modal.
 * Handles the three async UI states (loading, error, empty) as well as
 * the populated table with per-row host/rejoin chips and verified/unverified
 * status chip.
 */
export const ParticipantsList = ({ meetingDetails, loadingDetails, detailsError }) => {
    const { t, i18n } = useTranslation('shared');

    if (loadingDetails) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (detailsError) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {detailsError}
            </Alert>
        );
    }

    if (!meetingDetails?.participants?.list?.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {t('callLogs.noParticipantsFound')}
            </Typography>
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('callLogs.participantName')}</TableCell>
                        <TableCell>{t('callLogs.participantJoinTime')}</TableCell>
                        <TableCell>{t('callLogs.participantLeaveTime')}</TableCell>
                        <TableCell>{t('callLogs.participantStatus')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {meetingDetails.participants.list.map((participant, index) => (
                        <TableRow key={participant.id || index} hover>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" noWrap>
                                        {participant.name}
                                    </Typography>
                                    {participant.role === 'host' && (
                                        <Chip
                                            size="small"
                                            label={t('callLogs.hostChip')}
                                            color="primary"
                                            variant="outlined"
                                            sx={{ height: 20, flexShrink: 0 }}
                                        />
                                    )}
                                    {participant.is_rejoin && (
                                        <Chip
                                            size="small"
                                            label={t('callLogs.rejoinedChip')}
                                            color="warning"
                                            variant="outlined"
                                            sx={{ height: 20, flexShrink: 0 }}
                                        />
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {formatTimestamp(participant.join_time, i18n.language).time}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {participant.leave_time ? formatTimestamp(participant.leave_time, i18n.language).time : '-'}
                            </TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={participant.isUnverified ? t('callLogs.unverified') : t('callLogs.verified')}
                                    color={participant.isUnverified ? 'error' : 'success'}
                                    icon={participant.isUnverified ? <CloseIcon /> : <CheckIcon />}
                                    sx={{ height: 24 }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
